import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SITE } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AdminLogin() {
  usePageMeta({
    title: "Owner Login | Swift Luxury Walls",
    description: "Internal admin area.",
    canonicalPath: "/admin/login",
    noindex: true,
  });
  const { user, checking, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (checking) return <FullscreenLoader />;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back, Rob.");
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 grain pt-20 pb-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={SITE.logo} alt="Swift Luxury Walls" className="w-12 h-12 rounded-md object-cover" />
            <div className="text-left leading-tight">
              <div className="font-display text-lg text-white">Swift Luxury Walls</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Owner Dashboard</div>
            </div>
          </Link>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-7 md:p-9"
        >
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-4 h-4 text-[#D4AF37]" />
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Sign in</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl">Owner login</h1>
          <p className="text-sm text-[#A1A1AA] mt-2">Access leads and configure push notifications.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Email</label>
              <input
                type="email"
                autoComplete="username"
                className="input-dark mt-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@swiftluxurywalls.com"
                data-testid="admin-email"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                className="input-dark mt-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-gold w-full !py-3 mt-8 justify-center"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" /> Sign in
              </>
            )}
          </button>

          <div className="mt-6 text-center text-xs text-[#6b6b72]">
            <Link to="/" className="hover:text-[#D4AF37]">← Back to site</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#D4AF37]">
      <Loader2 className="w-8 h-8 animate-spin" />
    </div>
  );
}
